import type { ReactNode } from 'react';
import { Card, InlineText } from '@/core/ui';
import { useUpdateCourse } from '../application/queries';
import type { Course } from '../domain/course';
import {
  authorNameProblem,
  collapseSpaces,
  courseTitleProblem,
  descriptionProblem,
  formatTags,
  LIMITS,
  parseTags,
  tagsProblem,
  toAuthorName,
} from '../domain/schemas';
import { editProblem } from './problems';

interface Props {
  course: Course;
  /** Sem `cursos.editar`: só leitura. */
  canEdit: boolean;
}

/**
 * O título do curso, editável no lugar. O endereço (slug) não muda junto:
 * os links já compartilhados continuam abrindo o curso.
 */
export function CourseTitle({ course, canEdit, className }: Props & { className?: string }) {
  const update = useUpdateCourse(course.id);
  return (
    <InlineText
      label="título do curso"
      value={course.title}
      readOnly={!canEdit}
      maxLength={LIMITS.courseTitle.max}
      normalize={collapseSpaces}
      validate={courseTitleProblem}
      describeError={editProblem}
      onSave={(title) => update.mutateAsync({ title })}
      className={className}
    />
  );
}

/**
 * Sobre o curso: quem ministra, a descrição e as tags. Quem edita clica no
 * texto (ou no lápis); cada campo salva sozinho ao apertar Enter ou sair dele.
 */
export function CourseDetails({ course, canEdit }: Props) {
  const update = useUpdateCourse(course.id);
  const author = course.author_name ?? '';

  if (!canEdit && !author && !course.description && course.tags.length === 0) return null;

  return (
    <Card className="space-y-4 p-5">
      <h2 className="text-base font-semibold">Sobre o curso</h2>

      {(canEdit || author) && (
        <Detail label="Autor(a) / Professor(a)">
          <InlineText
            label="autor(a) do curso"
            value={author}
            placeholder="Quem ministra o curso"
            readOnly={!canEdit}
            maxLength={LIMITS.authorName}
            normalize={collapseSpaces}
            validate={authorNameProblem}
            describeError={editProblem}
            // Vazio apaga: o portal volta ao visual sem autor.
            onSave={(v) => update.mutateAsync({ author_name: toAuthorName(v) })}
            className="text-sm font-medium text-fg"
          />
        </Detail>
      )}

      {(canEdit || course.description) && (
        <Detail label="Descrição">
          <InlineText
            multiline
            label="descrição do curso"
            value={course.description}
            placeholder="Adicionar descrição"
            readOnly={!canEdit}
            maxLength={LIMITS.description}
            validate={descriptionProblem}
            describeError={editProblem}
            onSave={(description) => update.mutateAsync({ description })}
            className="text-sm leading-relaxed whitespace-pre-line text-fg-muted"
          />
        </Detail>
      )}

      {(canEdit || course.tags.length > 0) && (
        <Detail label="Tags">
          <InlineText
            label="tags do curso"
            value={course.tags.join(', ')}
            placeholder="Separe por vírgula: nutrologia, tireoide"
            readOnly={!canEdit}
            normalize={formatTags}
            validate={tagsProblem}
            describeError={editProblem}
            onSave={(v) => update.mutateAsync({ tags: parseTags(v) })}
            className="text-sm"
            display={
              course.tags.length > 0 ? (
                <span className="flex flex-wrap gap-1.5">
                  {course.tags.map((t) => (
                    <span key={t} className="rounded-full bg-surface-2 px-2.5 py-1 text-xs text-fg-muted">
                      {t}
                    </span>
                  ))}
                </span>
              ) : undefined
            }
          />
        </Detail>
      )}
    </Card>
  );
}

function Detail({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-1">
      <p className="text-xs font-medium text-fg-subtle">{label}</p>
      {children}
    </div>
  );
}
